import {type CSSProperties, forwardRef, type HTMLAttributes, type ReactNode} from "react";
import * as styles from "./AiAttributeRating.module.css";
import type {AiAttributeValueEntry} from "../../domain/VulSourceItem";
import {Tooltip} from "antd";

const RATING_COLORS: [number, [string, string]][] = [
    [30, ["#d9d9d9", "#262626"]],
    [50, ["#bfbfbf", "#262626"]],
    [70, ["#91caff", "#262626"]],
    [90, ["#69b1ff", "#262626"]],
    [Infinity, ["#4096ff", "#262626"]]
];

function percentToColors(percent: number): [string, string] {
    const match = RATING_COLORS.find(([threshold]) => percent < threshold);

    return match?.[1] ?? RATING_COLORS[RATING_COLORS.length - 1][1];
}

function formatPercent(percent: number): string {
    const decimals = (percent >= 10 || Number.isInteger(percent)) ? 0 : 1;

    return `${percent.toFixed(decimals)}%`;
}

function formatChipPercent(percent: number): string {
    return `${Math.round(percent)}%`;
}

export function AiAttributeRatingChipList(props: { children: ReactNode }) {
    return <div className={styles.chipList}>{props.children}</div>;
}

type AiAttributeRatingChipProps = { label: string, value: number } & Omit<HTMLAttributes<HTMLDivElement>, "className">;

export const AiAttributeRatingChip = forwardRef<HTMLDivElement, AiAttributeRatingChipProps>((props, ref) => {
    const {label, value, ...rest} = props;
    const percent = value * 100;

    const colors = percentToColors(percent);

    return (
        <div ref={ref} {...rest} className={styles.chip}>
            <div className={styles.chipFill} style={{width: `${percent}%`, background: colors[0]}}/>
            <span className={styles.chipLabel} style={{color: colors[1]}}>{label} {formatChipPercent(percent)}</span>
        </div>
    );
});
AiAttributeRatingChip.displayName = "AiAttributeRatingChip";

const OWNER_TAG_STYLE: CSSProperties = {
    display: "inline-block",
    padding: "0 5px",
    textAlign: "center",
    fontSize: 10,
    fontWeight: 600,
    borderRadius: 2,
    lineHeight: "16px",
    flexShrink: 0,
    color: "white",
};

export function OwnerTag(props: { owner?: string, organizationName?: string }) {
    if (props.owner === "ORGANIZATION") {
        return <span style={{...OWNER_TAG_STYLE, background: "#0066cc"}}>{props.organizationName ?? "organization"}</span>;
    }

    return <span style={{...OWNER_TAG_STYLE, background: "#440084"}}>system</span>;
}

export function AiExtractorEntryList(props: { entries: AiAttributeValueEntry[], renderValue?: (entry: AiAttributeValueEntry) => ReactNode }) {
    return (
        <div className={styles.entryList}>
            <div className={styles.entryListHeader}>
                AI extraction results
            </div>
            {props.entries.map((entry, i) => (
                <div key={i} className={styles.entryListItem}>
                    <span className={styles.entryListBody}>
                        <span className={styles.entryListTitle}>
                            {entry.aiExtractorId
                                ? <a href={`/definitions/ai-extractors/${entry.aiExtractorId}`}
                                     target="_blank" rel="noopener noreferrer"
                                     style={{color: "inherit"}}>{entry.aiExtractorName}</a>
                                : <span>{entry.aiExtractorName}</span>
                            }
                            <OwnerTag owner={entry.aiExtractorOwner} organizationName={entry.aiExtractorOrganizationName}/>
                            {props.renderValue && <b>{props.renderValue(entry)}</b>}
                        </span>
                        {entry.message && <span className={styles.entryListMessage}>{entry.message}</span>}
                    </span>
                </div>
            ))}
        </div>
    );
}

export function AiAttributeRatingMultiChip(props: { label: string, values: AiAttributeValueEntry[] }) {
    const sorted = [...props.values].sort((a, b) => (a.valueRating ?? 0) - (b.valueRating ?? 0));

    const ratings = sorted.map(v => v.valueRating ?? 0);
    const maxRating = Math.max(...ratings);
    const maxPercent = maxRating * 100;
    const colors = percentToColors(maxPercent);

    const valuesText = ratings.length === 1
        ? formatChipPercent(ratings[0] * 100)
        : [...new Set(ratings.map(r => Math.round(r * 100)))].join("·") + "%";

    const tooltipContent = (
        <AiExtractorEntryList entries={sorted} renderValue={v => formatPercent((v.valueRating ?? 0) * 100)}/>
    );

    return (
        <Tooltip title={tooltipContent} overlayStyle={{maxWidth: "none"}}>
            <div className={styles.chip}>
                <div className={styles.chipFill} style={{width: `${maxPercent}%`, background: colors[0]}}/>
                <span className={styles.chipLabel} style={{color: colors[1]}}>{props.label} {valuesText}</span>
            </div>
        </Tooltip>
    );
}
